import React, { useState, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { photos, collections, Photo } from '../data/photos';
import { PhotoDetail } from './PhotoDetail';
import { ProtectedImage } from './ProtectedImage';
type FilterCategory = 'all' | 'shadow' | 'awareness' | 'light';
const filters: {
  label: string;
  value: FilterCategory;
}[] = [{
  label: 'ALL',
  value: 'all'
}, {
  label: 'SHADOW',
  value: 'shadow'
}, {
  label: 'AWARENESS',
  value: 'awareness'
}, {
  label: 'LIGHT',
  value: 'light'
}];
export function Gallery() {
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterCategory>('all');
  const navigate = useNavigate();
  const filteredPhotos = activeFilter === 'all' ? photos : photos.filter(p => p.category === activeFilter);
  const lastPhoto = filteredPhotos[filteredPhotos.length - 1];
  const isLastPhotoVisible = lastPhoto?.id === '10';
  const gridPhotos = isLastPhotoVisible ? filteredPhotos.slice(0, -1) : filteredPhotos;
  const handlePhotoClick = (photo: Photo, e: React.MouseEvent) => {
    navigate(`/photo/${photo.id}`);
  };
  return <section id="collections" className="py-24 md:py-32 bg-white px-6 md:px-12" data-id="element-61">
      <div className="max-w-7xl mx-auto" data-id="element-62">
        <div className="text-center mb-20 md:mb-28" data-id="element-63">
          <h2 className="text-3xl md:text-5xl font-serif text-charcoal mb-4" data-id="element-64">
            Entre Luces y Sombras
          </h2>
          <p className="text-[10px] md:text-xs font-sans tracking-[0.25em] uppercase text-gray-400 mb-10" data-id="element-65">
            Between Light and Shadow
          </p>

          {/* Curatorial Text */}
          <div className="max-w-2xl mx-auto mb-12" data-id="element-66">
            <AnimatePresence mode="wait" data-id="element-67">
              <motion.p key={activeFilter} initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -10
            }} transition={{
              duration: 0.3
            }} className="text-sm md:text-base font-sans leading-relaxed text-gray-600" data-id="element-68">
                {collections[activeFilter]}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* Filter Pills */}
          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4" data-id="element-69">
            {filters.map(filter => <button key={filter.value} onClick={() => setActiveFilter(filter.value)} className={`px-6 py-2.5 rounded-full text-[10px] md:text-xs font-sans tracking-[0.15em] uppercase transition-all duration-300 ${activeFilter === filter.value ? 'bg-charcoal text-white shadow-md' : 'bg-transparent text-gray-400 border border-gray-200 hover:border-charcoal hover:text-charcoal'}`} data-id="element-70">
                {filter.label}
              </button>)}
          </div>
        </div>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12 md:gap-16" data-id="element-71">
          <AnimatePresence mode="popLayout" data-id="element-72">
            {gridPhotos.map((photo, index) => <motion.div key={photo.id} layout initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="group cursor-pointer" onClick={e => handlePhotoClick(photo, e)} data-id="element-73">
                <div className="relative overflow-hidden bg-offwhite aspect-[4/5] mb-6" data-id="element-74">
                  <ProtectedImage src={photo.imageUrl} alt={photo.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" wrapperClassName="w-full h-full" data-id="element-75" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-500 z-30 pointer-events-none" data-id="element-76" />
                </div>
                <div className="text-center" data-id="element-77">
                  <h3 className="text-lg font-serif text-charcoal mb-3 transition-colors group-hover:text-gray-600" data-id="element-78">
                    {photo.title}
                  </h3>
                  <p className="text-[10px] font-sans tracking-[0.15em] text-gray-400 uppercase" data-id="element-79">
                    {photo.location}, {photo.year}
                  </p>
                </div>
              </motion.div>)}
          </AnimatePresence>
        </motion.div>

        {/* Featured Title Piece - Rendered separately when visible */}
        {isLastPhotoVisible && lastPhoto && <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.3
      }} className="mt-16 pt-12 border-t border-gray-100" data-id="element-80">
            <div className="group cursor-pointer max-w-6xl mx-auto" onClick={e => handlePhotoClick(lastPhoto, e)} data-id="element-81">
              <div className="relative bg-gray-50 p-8 md:p-12 mb-8 flex items-center justify-center" data-id="element-82">
                <ProtectedImage src={lastPhoto.imageUrl} alt={lastPhoto.title} loading="lazy" className="w-full max-h-[70vh] object-contain transition-transform duration-700 group-hover:scale-[1.02]" wrapperClassName="w-full flex items-center justify-center" data-id="element-83" />
              </div>
              <div className="text-center" data-id="element-84">
                <h3 className="text-3xl md:text-4xl font-serif text-charcoal mb-4 transition-colors group-hover:text-gray-600" data-id="element-85">
                  {lastPhoto.title}
                </h3>
                <p className="text-xs font-sans tracking-[0.15em] text-gray-400 uppercase" data-id="element-86">
                  {lastPhoto.location}, {lastPhoto.year}
                </p>
              </div>
            </div>
          </motion.div>}
      </div>

      {selectedPhoto && <PhotoDetail photo={selectedPhoto} onClose={() => setSelectedPhoto(null)} isModal={true} data-id="element-87" />}
    </section>;
}